/**
 * get_action_schema tool - Get connector action schema details
 */

import { z } from "zod";
import type { SchemaCache } from "../cache/schema-cache.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { logger } from "../utils/logger.js";
import {
  boundedConnectorSchema,
  connectorAsyncExtensions,
  safeConnectorSchemaText,
} from "../utils/connector-schema.js";

export const getActionSchemaInputSchema = z.object({
  connectorId: z
    .string()
    .describe("The connector ID (e.g., 'shared_sharepointonline', 'shared_teams')"),
  operationId: z
    .string()
    .optional()
    .describe("Specific operation ID to get schema for. If omitted, lists all operations."),
  includeParameters: z
    .boolean()
    .optional()
    .describe("Include parameter details in the response (default true)"),
  useCache: z
    .boolean()
    .optional()
    .describe(
      "Use cached metadata when available (default true). Set false for live response and path-level async metadata.",
    ),
});

export type GetActionSchemaInput = z.infer<typeof getActionSchemaInputSchema>;

export const getActionSchemaTool = {
  name: "get_action_schema",
  description:
    "Get the schema and parameters for a connector's actions/triggers. Use search_connectors first to find connector IDs. Without operationId, lists operations. With operationId, returns bounded parameter and response schemas plus actual trigger/async notification metadata. Set useCache=false to read live path-level notification metadata, which the operation cache does not store. Schema examples and defaults are omitted; truncation and unresolved references are explicit.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connectorId: {
        type: "string",
        description: "The connector ID (e.g., 'shared_sharepointonline', 'shared_teams')",
      },
      operationId: {
        type: "string",
        description: "Specific operation ID to get schema for. If omitted, lists all operations.",
      },
      includeParameters: {
        type: "boolean",
        description: "Include parameter details in the response (default true)",
      },
      useCache: {
        type: "boolean",
        description:
          "Use cached metadata when available (default true). Set false for live response and path-level async metadata.",
      },
    },
    required: ["connectorId"],
  },
};

export interface OperationSummary {
  operationId: string;
  method: string;
  path: string;
  summary: string | null;
  description: string | null;
  isTrigger: boolean;
}

export interface OperationDetail {
  operationId: string;
  method: string;
  path: string;
  summary: string | null;
  description: string | null;
  isTrigger: boolean;
  parameters?: unknown[] | undefined;
  responses?: Record<string, unknown> | undefined;
  extensions?: Record<string, unknown> | undefined;
  pathExtensions?: Record<string, unknown> | undefined;
  fromCache?: boolean;
}

export interface GetActionSchemaContext {
  cache: SchemaCache | null;
  api: ConnectorMetadataApi;
}

export async function handleGetActionSchema(
  context: GetActionSchemaContext,
  input: unknown,
): Promise<string> {
  const parsed = getActionSchemaInputSchema.parse(input);
  const includeParameters = parsed.includeParameters !== false;

  logger.info(
    { connectorId: parsed.connectorId, operationId: parsed.operationId },
    "Executing get_action_schema",
  );

  // If specific operation requested
  if (parsed.operationId) {
    // Try cache first
    if (context.cache && parsed.useCache !== false) {
      const cachedOp = context.cache.getOperation(parsed.connectorId, parsed.operationId);
      if (cachedOp && cachedOp.schema) {
        return formatOperationDetail(
          {
            operationId: cachedOp.operationId,
            method: cachedOp.method ?? "UNKNOWN",
            path: cachedOp.path ?? "",
            summary: cachedOp.summary,
            description: cachedOp.description,
            isTrigger: cachedOp.isTrigger,
            parameters: includeParameters ? cachedOp.schema.parameters : undefined,
            responses: includeParameters ? cachedOp.schema.responses : undefined,
            extensions: connectorAsyncExtensions(cachedOp.schema),
            fromCache: true,
          },
          parsed.connectorId,
        );
      }
    }

    // Fall back to API
    const schema = await context.api.getActionSchema(parsed.connectorId, parsed.operationId);

    if (!schema) {
      return `Operation "${parsed.operationId}" not found in connector "${parsed.connectorId}".`;
    }

    return formatOperationDetail(
      {
        operationId: schema.operationId,
        method: schema.method,
        path: schema.path,
        summary: schema.summary ?? null,
        description: schema.description ?? null,
        isTrigger: schema.extensions?.["x-ms-trigger"] !== undefined,
        parameters: includeParameters ? (schema.parameters ?? undefined) : undefined,
        responses: includeParameters ? (schema.responses ?? undefined) : undefined,
        extensions: schema.extensions,
        pathExtensions: schema.pathExtensions,
      },
      parsed.connectorId,
    );
  }

  // List all operations for the connector
  let operations: OperationSummary[] = [];

  // Try cache first
  if (context.cache && parsed.useCache !== false) {
    const cachedOps = context.cache.getOperations(parsed.connectorId);
    if (cachedOps.length > 0) {
      operations = cachedOps.map((op) => ({
        operationId: op.operationId,
        method: op.method ?? "UNKNOWN",
        path: op.path ?? "",
        summary: op.summary,
        description: op.description,
        isTrigger: op.isTrigger,
      }));
    }
  }

  // Fall back to API if cache empty
  if (operations.length === 0) {
    const apiOperations = await context.api.getConnectorActions(parsed.connectorId);
    operations = apiOperations.map((op) => ({
      operationId: op.operationId,
      method: op.method,
      path: op.path,
      summary: op.summary ?? null,
      description: op.description ?? null,
      isTrigger: op.isTrigger,
    }));
  }

  return formatOperationsList(operations, parsed.connectorId);
}

function formatOperationDetail(op: OperationDetail, connectorId: string): string {
  const lines = [
    `## ${op.operationId}`,
    ``,
    `**Connector:** ${connectorId}`,
    `**Method:** ${op.method} ${op.path}`,
    op.isTrigger ? `**Type:** Trigger` : `**Type:** Action`,
  ];

  if (op.summary) lines.push(`**Summary:** ${op.summary}`);
  if (op.description) lines.push(`**Description:** ${op.description}`);
  if (op.fromCache)
    lines.push(
      "Cached operation: path-level notification metadata is unavailable here. Set useCache=false for live metadata.",
    );

  if (op.parameters && Array.isArray(op.parameters) && op.parameters.length > 0) {
    lines.push(``, `### Parameters`);
    const parameters = boundedConnectorSchema(op.parameters) as Array<
      | {
          name?: string;
          in?: string;
          required?: boolean;
          type?: string;
          description?: string;
          enum?: unknown[];
          $ref?: string;
          schema?: {
            type?: string;
            enum?: unknown[];
            properties?: Record<string, unknown>;
            required?: string[];
          };
          "x-ms-summary"?: string;
          "x-ms-visibility"?: string;
          "x-ms-notification-url"?: boolean;
        }
      | string
    >;
    for (const p of parameters) {
      if (!p || typeof p !== "object") {
        lines.push("… parameter schema truncated");
        continue;
      }
      // A parameter should already be $ref-resolved upstream; if one slips
      // through, show the ref target rather than "undefined".
      const name = p.name ?? p.$ref ?? "(unnamed)";
      const where = p.in ?? "unknown";
      const req = p.required ? " (required)" : "";
      const typeValue = p.type ?? p.schema?.type;
      const type = typeValue ? ` [${typeValue}]` : "";
      const visibility = p["x-ms-visibility"] === "internal" ? " (internal)" : "";
      lines.push(`• **${name}**${type}${req}${visibility} - ${where}`);
      const description = p.description ?? p["x-ms-summary"];
      if (description) lines.push(`  ${description}`);
      if (typeof p["x-ms-notification-url"] === "boolean")
        lines.push(`  x-ms-notification-url: ${p["x-ms-notification-url"]}`);
      // Static enum constraints bite hard (e.g. RunScriptProd pins
      // ScriptParameters to [null] outside the designer) — surface them.
      const enumValues = p.enum ?? p.schema?.enum;
      if (enumValues && enumValues.length > 0) {
        lines.push(`  Allowed values: ${JSON.stringify(boundedConnectorSchema(enumValues))}`);
      }
      // A body parameter is only useful with its fields: "emailMessage (required) - body"
      // says nothing about To/Subject/Body. The API layer inlines the definition;
      // list its properties (two levels) with required markers and descriptions.
      if (where === "body" && p.schema && typeof p.schema === "object") {
        lines.push(...renderBodyProperties(p.schema, "  ", 0));
      }
    }
  }

  if (op.responses && Object.keys(op.responses).length > 0) {
    lines.push(
      "",
      "### Responses",
      "Bounded schema metadata; examples/defaults omitted.",
      "```json",
      JSON.stringify(boundedConnectorSchema(op.responses, {}, { preserveRootKeys: true }), null, 2),
      "```",
    );
  }
  for (const [location, extensions] of [
    ["Operation", op.extensions],
    ["Path/resource", op.pathExtensions],
  ] as const) {
    if (extensions && Object.keys(extensions).length) {
      lines.push(
        "",
        `### ${location} trigger / async metadata`,
        "Only extensions returned by the connector; no action kind is inferred.",
        "```json",
        JSON.stringify(boundedConnectorSchema(extensions), null, 2),
        "```",
      );
    }
  }
  // Redact before clipping so a truncation boundary cannot reveal part of a token.
  const result = lines
    .flatMap((line) =>
      line.split("\n").map((part) => {
        const safe = safeConnectorSchemaText(part);
        return safe.length > 4000 ? `${safe.slice(0, 4000)} [truncated]` : safe;
      }),
    )
    .join("\n");
  return result.length > 24000
    ? `${result.slice(0, 23940)}\n… output truncated (24,000 character limit)`
    : result;
}

function renderBodyProperties(
  schema: Record<string, unknown>,
  indent: string,
  depth: number,
): string[] {
  const unresolved =
    typeof schema["$ref"] === "string"
      ? [`${indent}Unresolved schema reference: ${schema["$ref"]}`]
      : [];
  const props = schema["properties"];
  if (!props || typeof props !== "object") return unresolved;
  if (depth > 1)
    return Object.keys(props).length
      ? [`${indent}… nested schema fields truncated at depth limit`]
      : [];
  const required = new Set(
    Array.isArray(schema["required"]) ? (schema["required"] as unknown[]).map(String) : [],
  );
  const out: string[] = [...unresolved];
  const entries = Object.entries(props as Record<string, unknown>);
  for (const [name, raw] of entries.slice(0, 40)) {
    const prop = (raw ?? {}) as Record<string, unknown>;
    const type =
      typeof prop["type"] === "string"
        ? ` [${prop["type"]}${prop["format"] ? `:${String(prop["format"])}` : ""}]`
        : "";
    const req = required.has(name) ? " (required)" : "";
    const visibility =
      prop["x-ms-visibility"] === "internal"
        ? " (internal)"
        : prop["x-ms-visibility"] === "advanced"
          ? " (advanced)"
          : "";
    const label = typeof prop["x-ms-summary"] === "string" ? prop["x-ms-summary"] : undefined;
    const description = typeof prop["description"] === "string" ? prop["description"] : undefined;
    const text = [label, description].filter((v) => v && v !== name).join(" — ");
    out.push(`${indent}- ${name}${type}${req}${visibility}${text ? `: ${text}` : ""}`);
    if (typeof prop["$ref"] === "string")
      out.push(`${indent}    Unresolved schema reference: ${prop["$ref"]}`);
    const enumValues = prop["enum"];
    if (Array.isArray(enumValues) && enumValues.length > 0)
      out.push(
        `${indent}    Allowed values: ${JSON.stringify(boundedConnectorSchema(enumValues))}`,
      );
    if (typeof prop["x-ms-notification-url"] === "boolean")
      out.push(`${indent}    x-ms-notification-url: ${prop["x-ms-notification-url"]}`);
    if (prop["properties"]) out.push(...renderBodyProperties(prop, indent + "    ", depth + 1));
    const items = prop["items"];
    if (items && typeof items === "object" && (items as Record<string, unknown>)["properties"]) {
      out.push(
        ...renderBodyProperties(items as Record<string, unknown>, indent + "    ", depth + 1),
      );
    }
  }
  if (entries.length > 40) out.push(`${indent}… ${entries.length - 40} more properties`);
  return out;
}

function formatOperationsList(operations: OperationSummary[], connectorId: string): string {
  if (operations.length === 0) {
    return `No operations found for connector "${connectorId}".`;
  }

  const triggers = operations.filter((o) => o.isTrigger);
  const actions = operations.filter((o) => !o.isTrigger);

  const lines = [
    `## ${connectorId}`,
    ``,
    `${triggers.length} triggers, ${actions.length} actions`,
    ``,
  ];

  if (triggers.length > 0) {
    lines.push(`### Triggers`);
    for (const t of triggers) {
      lines.push(`• **${t.operationId}** - ${t.method} ${t.path}`);
      if (t.summary) lines.push(`  ${t.summary}`);
    }
    lines.push(``);
  }

  if (actions.length > 0) {
    lines.push(`### Actions`);
    for (const a of actions) {
      lines.push(`• **${a.operationId}** - ${a.method} ${a.path}`);
      if (a.summary) lines.push(`  ${a.summary}`);
    }
  }

  return lines.join("\n");
}
